import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../../../App';
import { isHDLectureManager, hasHDIndustry } from '../../../config/hdLectureManager';
import {
  MDBCard,
  MDBCardBody,
  MDBTypography,
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsContent,
  MDBTabsPane,
  MDBSpinner,
  MDBBtn,
  MDBToast,
  MDBIcon
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';

import GeneralTab from './GeneralTab';
import LocationTab from './LocationTab';
import CarpetTab from './CarpetTab';
import BoothTab from "./BoothTab";
import HallPlanTab from "./HallPlanTab";
import TextsTab from './TextsTab';
import MarketingTab from './MarketingTab';
import EquipmentTab from './EquipmentTab';
import DocumentsTab from './DocumentsTab';
import CopyFromEventModal from './CopyFromEventModal';
import { useEventData } from './hooks/useEventData';
import { useIndustries } from './hooks/useIndustries';
import { captureError } from '../../../utils/sentryHelpers';

const EditEvent = () => {
  const { eventId } = useParams();
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [showToast, setShowToast] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [reinitializeEditors, setReinitializeEditors] = useState(0);

  const {
    event,
    setEvent,
    isLoading: isEventLoading,
    loadError: eventLoadError,
    updateEvent,
    handleInputChange,
    handleImageUpload,
    handleBadgePreviewUpload,
    handleFileUpload,
    handleRemoveFile,
    handleFileDownload,
    handleProgramUpload,
    handleProgramRemove,
    handleProgramDownload,
    handleHighlightVideoUpload,
    handleHighlightImageUpload,
    handlePressMediaUpload
  } = useEventData(eventId);

  const initialSelectedIndustryIds = useMemo(() =>
    event?.industries?.items?.map(item => item.industry.id) || [],
    [event?.industries?.items]
  );

  useEffect(() => {
    if (!isEventLoading && eventId && event && isHDLectureManager(user) && !hasHDIndustry(event)) {
      navigate('/', { replace: true });
    }
  }, [isEventLoading, eventId, event, user, navigate]);

  const {
    industries,
    selectedIndustryIds,
    setSelectedIndustryIds,
    isLoading: isIndustriesLoading,
    updateEventIndustries
  } = useIndustries(eventId, initialSelectedIndustryIds);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // First save the event
      await updateEvent();
      // Then update the industries if we have an eventId
      if (eventId) {
        await updateEventIndustries(eventId);
      }
      setShowToast(true);
    } catch (error) {
      captureError(error, {
        feature: 'event-management',
        operation: 'save-event',
        context: { eventId },
        tags: { component: 'EditEvent' }
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplyFromEvent = (selectedFields, sourceEvent, sourceIndustryIds) => {
    // Event-Felder übernehmen
    const newEventData = {};

    // Helper um nested values zu holen
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((current, key) => current?.[key], obj);
    };

    // Sammle Location-Subfelder
    const locationFields = selectedFields.filter(f => f.startsWith('location.'));
    const otherFields = selectedFields.filter(f => !f.startsWith('location.') && f !== 'industries');

    // Normale Felder übernehmen
    otherFields.forEach(fieldKey => {
      const value = sourceEvent[fieldKey];
      if (value !== undefined && value !== null) {
        newEventData[fieldKey] = value;
      }
    });

    // Location-Felder zusammenbauen wenn welche ausgewählt wurden
    if (locationFields.length > 0) {
      const newLocation = { ...(event.location || {}) };

      locationFields.forEach(fieldKey => {
        const subKey = fieldKey.replace('location.', '');
        const value = getNestedValue(sourceEvent, fieldKey);
        if (value !== undefined && value !== null) {
          newLocation[subKey] = value;
        }
      });

      newEventData.location = newLocation;
    }

    setEvent(prev => ({ ...prev, ...newEventData }));

    // Industries separat übernehmen
    if (selectedFields.includes('industries') && sourceIndustryIds) {
      setSelectedIndustryIds(sourceIndustryIds);
    }

    // Trigger re-initialization of WYSIWYG editors in TextsTab
    setReinitializeEditors(prev => prev + 1);

    setShowCopyModal(false);
  };

  if (isEventLoading || isIndustriesLoading) {
    return <MDBSpinner />;
  }

  if (eventId && eventLoadError) {
    return (
      <MDBCard>
        <MDBCardBody className="text-center py-5">
          <MDBIcon fas icon="exclamation-triangle" size="3x" className="text-danger mb-3" />
          <MDBTypography tag="h4" className="text-danger mb-3">
            {t('Error loading event')}
          </MDBTypography>
          <p className="text-muted mb-4">
            {t('The event could not be loaded. Please try again or contact support if the problem persists.')}
          </p>
          <MDBBtn color="primary" onClick={() => window.location.reload()}>
            <MDBIcon fas icon="redo" className="me-2" />
            {t('Retry')}
          </MDBBtn>
        </MDBCardBody>
      </MDBCard>
    );
  }

  return (
    <>
      <MDBCard>
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <MDBTypography tag="h2" className="mb-0">
                {eventId ? t('Edit Event') : t('Create Event')}
              </MDBTypography>
              {!eventId && (
                <MDBBtn
                  color="secondary"
                  outline
                  onClick={() => setShowCopyModal(true)}
                  className="ms-3"
                >
                  <MDBIcon fas icon="copy" className="me-2" />
                  {t('Copy from event')}
                </MDBBtn>
              )}
            </div>
            <MDBBtn
              onClick={handleSave}
              disabled={isSaving}
              className="ms-2"
            >
              {isSaving ? (
                <>
                  <MDBSpinner size='sm' className='me-2' />
                  {t('Saving')}
                </>
              ) : (
                t('Save')
              )}
            </MDBBtn>
          </div>

          <MDBTabs className="mb-3">
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("general")} active={activeTab === 'general'}>
                {t("General")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("texts")} active={activeTab === 'texts'}>
                {t("Texts")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("location")} active={activeTab === 'location'}>
                {t("Location")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("carpet")} active={activeTab === 'carpet'}>
                {t("Carpet")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("booths")} active={activeTab === 'booths'}>
                {t("Booths")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("hallplan")} active={activeTab === 'hallplan'}>
                {t("Hall plan & booths")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("marketing")} active={activeTab === 'marketing'}>
                {t("Marketing Products")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("equipment")} active={activeTab === 'equipment'}>
                {t("Equipment Products")}
              </MDBTabsLink>
            </MDBTabsItem>
            <MDBTabsItem>
              <MDBTabsLink onClick={() => setActiveTab("documents")} active={activeTab === 'documents'}>
                {t("Documents")}
              </MDBTabsLink>
            </MDBTabsItem>
            <li className="nav-item" role="presentation" style={{ cursor: "pointer" }}>
              <Link to={`/admin/product/${event.ticketProduct?.id}`} className="nav-link">Ticket Product <MDBIcon fas icon="external-link-alt" /></Link>
            </li>
          </MDBTabs>

          <MDBTabsContent>
            <MDBTabsPane open={activeTab === 'general'}>
              <GeneralTab
                event={event}
                industries={industries}
                selectedIndustryIds={selectedIndustryIds}
                setSelectedIndustryIds={setSelectedIndustryIds}
                onInputChange={handleInputChange}
                onImageUpload={handleImageUpload}
                onBadgePreviewUpload={handleBadgePreviewUpload}
                onFileUpload={handleFileUpload}
                onRemoveFile={handleRemoveFile}
                onDownloadFile={handleFileDownload}
                onProgramUpload={handleProgramUpload}
                onProgramRemove={handleProgramRemove}
                onProgramDownload={handleProgramDownload}
                isLoading={isEventLoading || isIndustriesLoading}
              />
            </MDBTabsPane>

            <MDBTabsPane open={activeTab === 'texts'}>
              <TextsTab
                event={event}
                onInputChange={handleInputChange}
                reinitializeEditors={reinitializeEditors}
                onHighlightVideoUpload={handleHighlightVideoUpload}
                onHighlightImageUpload={handleHighlightImageUpload}
                onPressMediaUpload={handlePressMediaUpload}
                onFileDownload={handleProgramDownload}
              />
            </MDBTabsPane>

            <MDBTabsPane open={activeTab === 'location'}>
              <LocationTab
                event={event}
                onInputChange={handleInputChange}
              />
            </MDBTabsPane>

            <MDBTabsPane open={activeTab === 'carpet'}>
              <CarpetTab
                eventId={eventId}
                event={event}
              />
            </MDBTabsPane>
            <MDBTabsPane open={activeTab === 'booths'}>
              <BoothTab
                eventId={eventId}
              />
            </MDBTabsPane>
            <MDBTabsPane open={activeTab === 'hallplan'}>
              <HallPlanTab
                eventId={eventId}
                isActive={activeTab === 'hallplan'}
              />
            </MDBTabsPane>
            <MDBTabsPane open={activeTab === 'marketing'}>
              <MarketingTab
                eventId={eventId}
              />
            </MDBTabsPane>
            <MDBTabsPane open={activeTab === 'equipment'}>
              <EquipmentTab
                eventId={eventId}
              />
            </MDBTabsPane>
            <MDBTabsPane open={activeTab === 'documents'}>
              <DocumentsTab
                event={event}
                onInputChange={handleInputChange}
              />
            </MDBTabsPane>
          </MDBTabsContent>
        </MDBCardBody>
      </MDBCard>

      <MDBToast
        color='success'
        open={showToast}
        offset={50}
        onClose={() => setShowToast(false)}
        autohide
        delay={30000}
        position='top-right'
        appendToBody
        bodyContent={<>
          {t('Event saved successfully!')}
        </>}
      >

      </MDBToast>

      {!eventId && (
        <CopyFromEventModal
          open={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          onApply={handleApplyFromEvent}
          allIndustries={industries}
        />
      )}
    </>
  );
};

export default EditEvent;